import React, { useState, useEffect } from 'react';
import {
  Button,
  ETIDialog, ETIDialogBody,
  ETIDialogHeader,
  IconButton, SearchableSelect,
  Select,
  TextField,
  Typo,
} from '@zawarski/eti-typescript-components';
import { useMediaQuery } from '@react-hook/media-query';
import { Paper, Box, Stack, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Controller, SubmitHandler } from 'react-hook-form';
import { toast } from 'sonner';
import { BREAKPOINT_700PX } from 'constant/breakpoints';
import useContactForm from './hooks/useContactForm';
import { SupportSubject } from '@/common/enum';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { accountSelector, activeGroupSelector } from 'store/account/account.selector';
import { postSupportMsg } from 'store/account/account.thunk';
import CloseIcon from '@mui/icons-material/Close';

type ContactSupportViewProps = {
  open: boolean;
  onClose: () => void;
  programsList: Nullable<ISelectedPrograms[]>;
};

const ContactSupportView: React.FC<ContactSupportViewProps> = ({ open, onClose, programsList }) => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(`only screen and (max-width: ${BREAKPOINT_700PX})`);
  const theme = useTheme();
  const form = useContactForm();
  const account = useAppSelector(accountSelector);
  const { control, handleSubmit, formState } = form;
  const formErrorGroup = formState.errors?.group?.message;
  const [inProcess, setInProcess] = useState(false);
  const userActiveGroupId = Number(useAppSelector(activeGroupSelector));
  const [programOptions, setProgramOptions] =
    useState<Nullable<{ value: number; label: string }[]>>(null);

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    try {
      setInProcess(true);
      const programLabel = programsList?.find(
        (program) => Number(program.id) === Number(data.programId)
      )?.label;
      const payload: ContactFormData = {
        email: data.email,
        name: data.name,
        subject: data.subject + (programLabel ? ` - ${programLabel}` : ''),
        group: data.group,
        positions: data.positions,
        message: data.message.replace(/\r\n|\r|\n/g, '<br />'),
      };
      await dispatch(postSupportMsg(payload)).unwrap();

      toast.success('Successfully Processed');
      const resetValues = getInitialFormValues();
      if (resetValues) {
        form.reset(resetValues);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Encountered System Error';
      toast.error(errorMessage);
    } finally {
      window.dispatchEvent(new CustomEvent('close-contact-support'));
      setInProcess(false);
    }
  };

  const getInitialFormValues = (): ContactFormData | null => {
    if (!account || !userActiveGroupId) return null;

    const currentGroup = account.currentGroups?.find((v) => v.i === userActiveGroupId);
    const positions = account.currentPositions?.map((v) => v.n) || [];

    return {
      name: `${account.pvPersonGivenName} ${account.pvPersonSurName}`,
      email: account.email || '',
      group: currentGroup?.n || '',
      positions,
      message: '',
      subject: '',
      programId: '',
    };
  };

  useEffect(() => {
    if (!account || !userActiveGroupId) return;

    const currentGroup = account.currentGroups?.find((v) => v.i === userActiveGroupId);
    const positions = account.currentPositions?.map((v) => v.n) || [];

    form.reset({
      name: `${account.pvPersonGivenName} ${account.pvPersonSurName}`,
      email: account.email || '',
      group: currentGroup?.n || '',
      positions,
      message: '',
      subject: '',
      programId: '',
    });
  }, [account, userActiveGroupId, form]);

  useEffect(() => {
    if (!programsList?.length) return;
    const contactPrograms = [...programsList];
    if (!contactPrograms.some((program) => program.id === 999)) {
      contactPrograms.push({
        id: 999,
        label: 'Dashboard',
        programName: 'dashboard',
        isActive: true,
      });
    }
    const programsOptions = contactPrograms
      .map((program) => ({
        value: Number(program.id),
        label: program.label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setProgramOptions(programsOptions);
  }, [programsList]);

  const supportSubjectsOptions = Object.entries(SupportSubject).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  function ProgressIcon() {
    return <CircularProgress color='inherit' size={22} sx={{ mr: 0.5 }} />;
  }

  return (
    <ETIDialog
      open={open}
      onClose={onClose}
      children={[
        <ETIDialogHeader
          key={'contactSupportHeader'}
          title={'Feel free to contact us'}
          titleVariant={'h5'}
          titleColor={theme.palette.primary.dark}
          subtitle={''}
          subtitleVariant={'subtitle2'}
          subtitleColor={'#707070'}
          closeButton={
            <IconButton
              color={'secondary'}
              size={'small'}
              onClick={onClose}
              children={<CloseIcon sx={{ color: '#707070' }} key={'closeIcon'} />}
            />
          }
        />,
        <ETIDialogBody
          key={'contactSupportBody'}
          children={[
            <div
              className={`flex flex-col ${isMobile ? '' : 'gap-6'} rounded-xl bg-lynch-50`}
              key={'dialogSupport'}>
              <Paper
                elevation={3} >
                <Stack direction='column' spacing={2}>
                  {formErrorGroup && formErrorGroup.length ? (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.error.main}`,
                        px: 4,
                        py: 1,
                        textAlign: 'center',
                      }}>
                      <Typo color={theme.palette.error.main} fontSize={16}>
                        {formErrorGroup}
                      </Typo>
                    </Box>
                  ) : null}
                  <Typo variant='body1' color='common.black'>
                    A response will be provide within 2 working days.
                  </Typo>
                  <Controller
                    control={control}
                    name={'subject'}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        fullWidth={true}
                        label={'Subject'}
                        placeholder={'Select Subject'}
                        value={value}
                        onChange={onChange}
                        selectOptions={supportSubjectsOptions}
                        error={Boolean(error)}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={'programId'}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        fullWidth={true}
                        label={'Program'}
                        placeholder={'Select Program'}
                        value={value}
                        onChange={onChange}
                        selectOptions={programOptions}
                        error={Boolean(error)}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={'name'}
                    render={({ field: { value }, fieldState: { error } }) => (
                      <TextField
                        error={Boolean(error)}
                        label='Name'
                        value={value}
                        helperText={error?.message}
                        disabled={true}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={'email'}
                    render={({ field: { value }, fieldState: { error } }) => (
                      <TextField
                        error={Boolean(error)}
                        label='Email address'
                        value={value}
                        disabled={true}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={'message'}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <TextField
                        error={Boolean(error)}
                        label='Details'
                        multiline
                        value={value}
                        onChange={onChange}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Button endIcon={inProcess ? <ProgressIcon /> : ''} onClick={handleSubmit(onSubmit)}>
                    {!inProcess ? 'Submit' : 'Please wait ...'}
                  </Button>
                </Stack>
              </Paper>
            </div>,
          ]}
        />

      ]}
    />
  );
};

export default ContactSupportView;
